import React from "react"
import { GatsbyLinkProps, withPrefix } from "gatsby"
import Tippy from "@tippyjs/react"
import { MDXProvider } from "@mdx-js/react"
import MDXRenderer from "./mdx-renderer"
import { LinkToStacked } from "react-stacked-pages-hook"
import { CSS as LinkClasses } from "gatsby-remark-double-parenthesis-link"

import "./anchor-tag.css"
import { isFullUrl } from "../../utils"
import { Block } from "../css-classes"
import { BlockProps, Block as BlockElement } from "./block"

interface OutboundReference {
  mdx: string,
  title: string,
  id: string,
  displayTitle: string,
  slug: string,
}

interface AnchorTagProps extends GatsbyLinkProps<any> {
  href?: string
  references: Array<OutboundReference>
  withoutLink: boolean
  withoutPopup: boolean
  /**
   * Mainly tracked to prevent infinite recursion
   */
  visited?: Set<string | undefined>
  /**
   * The idea here is to render links,
   * but not popups and don't try to render recursively
   * maybe try to render block references though?
   */
  simplified: boolean
}

export const defaultA = ({ href, ...restProps }: { href: string }) => {
  return <a
    {...restProps}
    className="external-link"
    // href={(!href || (href?.indexOf("#") === 0)) ? href : withPrefix(href)}
    href={href}
  />
}


export const AnchorTag = (
  {
    href,
    references = [],
    visited = new Set(),
    withoutLink,
    withoutPopup,
    // setting this to true doesn't actually help with perf much
    // It seems just "many things  rendering" 🤔
    // so need to look at something like react virtualized/etc
    simplified,
    ...restProps
  }: AnchorTagProps) => {
  let content = restProps.children

  const internalLink = (to: string = href!!) =>
    <LinkToStacked {...restProps} to={to}>
      {content}
    </LinkToStacked>

  if (isFullUrl(href)) return defaultA({ href, ...restProps })

  if (visited.has(href)) {
    return <span className="self-reference">SELF({content})</span>
  }

  /**
   * Allow second order referencing for block references. Will check impact
   */
  if (simplified && !isReferenceOrEmbed(restProps.className)) {
    return internalLink()
  }

  const ref = references.find((x) => withPrefix(x.slug) === withPrefix(href))

  if (withoutLink) {
    return <span>{content}</span>
  }
  let popupContent

  let child

  function blockEmbedClass() {
    return isBlockEmbed(restProps.className) ? ` ${LinkClasses.blockEmbed}` : ""
  }

  if (ref) {
    const refContent = () => <div className={Block.referenceContent}>
      <MDXProvider
        components={nestedComponents(references, visited, href, isBlockReference(restProps.className))}
      >
        <MDXRenderer>{ref.mdx}</MDXRenderer>
      </MDXProvider>
    </div>

    // current hypothesis of what this means
    let linkToLocalPage = ref.title === ref.displayTitle

    popupContent = (
      <div id={ref.id} className={"popover with-markdown" + blockEmbedClass()}>
        <React.Fragment>
          {!linkToLocalPage && <h5>{ref.displayTitle}</h5>}
          {refContent()}
        </React.Fragment>
      </div>
    )

    if (isReferenceOrEmbed(restProps.className)) {
      content = <React.Fragment>
        {refContent()}
      </React.Fragment>

    }
    child = internalLink(ref.slug)
  } else {
    /**
     * {@link useStackedPagesProvider} see how that loads page data
     */
    popupContent = <div className="popover no-max-width">{href}</div>
    child = internalLink()
  }

  if (withoutPopup) {
    return child
  }

  return (
    <Tippy interactive={true} delay={200} animation="shift-away" content={popupContent} maxWidth="none">
      {child}
    </Tippy>
  )
}

const hasClass = (classNames: string | undefined, toCheck: string) => classNames?.includes(toCheck)

const isBlockReference = (className?: string) => hasClass(className, LinkClasses.blockReference)
const isBlockEmbed = (className?: string) => hasClass(className, LinkClasses.blockEmbed)
const isReferenceOrEmbed = (className?: string) => isBlockReference(className) || isBlockEmbed(className)

/**
 * This is IMPORTANT. In connection with short circuit above
 * this prevents the infinite rendering recursion for block references
 * referring to themselves
 */
const nestedComponents = (
  references: Array<OutboundReference>,
  visited: Set<string | undefined>,
  href: string,
  referenceMode: boolean) => ({
  a(props: AnchorTagProps) {
    return <AnchorTag {...props} references={references} visited={new Set([...visited, href])} simplified />
  },
  p(props) {
    return <span {...props} />
  },
  Block(props: BlockProps) {
    return <BlockElement {...props} referenceMode={referenceMode}/>
  },
})
